import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import MuiLink from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import { ApiError } from '../lib/api'

const ERROR_MESSAGES: Record<string, string> = {
  USER_NOT_PROVISIONED: 'החשבון אינו קיים במערכת. יש לבקש מאדמין להוסיף אותך.',
  USER_INACTIVE: 'החשבון מושבת. יש לפנות לאדמין.',
  INVALID_STATE: 'תוקף בקשת ההתחברות פג. נסה שוב.',
  GOOGLE_SIGN_IN_FAILED: 'ההתחברות עם Google נכשלה. נסה שוב.',
  LOGIN_FAILED: 'ההתחברות נכשלה עקב תקלה בשרת. נסה שוב, ואם זה חוזר — יש לפנות לאדמין.',
  NO_CODE: 'ההתחברות בוטלה.',
  access_denied: 'ההתחברות בוטלה.',
}

export function LoginPage() {
  const { user, config, loading, signInWithDevLogin } = useAuth()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const returnTo = searchParams.get('returnTo') ?? '/'
  const urlError = searchParams.get('error')

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (user) return <Navigate to={returnTo.startsWith('/') ? returnTo : '/'} replace />

  const handleDevLogin = async (event: React.FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      await signInWithDevLogin(email.trim())
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'ההתחברות נכשלה')
    } finally {
      setSubmitting(false)
    }
  }

  const googleHref = `/api/auth/google?returnTo=${encodeURIComponent(returnTo)}`

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        px: 2,
        bgcolor: 'background.default',
      }}
    >
      <Paper sx={{ p: { xs: 3, sm: 5 }, width: '100%', maxWidth: 440 }} elevation={2}>
        <Box
          component="img"
          src="/logo.svg"
          alt=""
          sx={{ width: 56, height: 56, display: 'block', mb: 2 }}
        />
        <Typography variant="h5" sx={{ mb: 0.5 }}>
          ניהול משרד עורכי דין
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          חוזי מקרקעין · מערכת פנימית
        </Typography>

        {urlError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {ERROR_MESSAGES[urlError] ?? 'ההתחברות נכשלה'}
          </Alert>
        ) : null}
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {config.googleEnabled ? (
          <Button href={googleHref} variant="contained" size="large" fullWidth>
            התחברות עם Google
          </Button>
        ) : (
          <Alert severity="info">
            התחברות עם Google אינה מוגדרת בשרת. יש למלא GOOGLE_CLIENT_ID ו־GOOGLE_CLIENT_SECRET.
          </Alert>
        )}

        {config.devLoginEnabled ? (
          <>
            <Divider sx={{ my: 3 }}>התחברות לפיתוח</Divider>
            <Alert severity="warning" sx={{ mb: 2 }}>
              מסלול זה פעיל בסביבת פיתוח בלבד ואינו קיים בסביבת ייצור.
            </Alert>
            <Box component="form" onSubmit={handleDevLogin}>
              <Stack spacing={2}>
                <TextField
                  label="דואר אלקטרוני של משתמש קיים"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
                <Button type="submit" variant="outlined" disabled={submitting || !email.trim()}>
                  {submitting ? 'מתחבר…' : 'התחברות'}
                </Button>
              </Stack>
            </Box>
          </>
        ) : null}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
          <MuiLink href="/privacy">מדיניות פרטיות</MuiLink>
        </Typography>
      </Paper>
    </Box>
  )
}
